import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    // Listen for clicks on the cart drawer trigger button
    document.addEventListener('click', this.#handleTriggerClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('click', this.#handleTriggerClick);
  }

  #handleTriggerClick = (event) => {
    const trigger = event.target.closest('[data-cart-drawer-trigger]');
    if (trigger) {
      event.preventDefault();
      this.showDialog();
    }
  };

  #handleCartAdd = (event) => {
    // Update the cart drawer content if sections are provided
    // Get section ID dynamically from cart-items-component (inherits from parent section)
    const cartItemsEl = this.querySelector('cart-items-component');
    const sectionId = cartItemsEl?.dataset?.sectionId;
    if (sectionId) {
      const cartDrawerHtml = event.detail.data?.sections?.[sectionId];
      if (cartDrawerHtml) {
        morphSection(sectionId, cartDrawerHtml);
      }
    }

    if (this.hasAttribute('auto-open')) {
      // Wait for the next animation frame to ensure the cart DOM has been updated
      // before opening the drawer. This prevents showing stale content.
      requestAnimationFrame(() => {
        this.showDialog();
      });
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
